import React, { useState } from 'react';
import {Button, Group, Loader, Radio, ScrollArea, Stack, Text, Title} from '@mantine/core';
import SCModal from '@/PageComponents/Modal/SCModal';

// Confirms deactivating a contact. For each role the contact holds (primary, accounting), the other
// active contacts are passed in as candidates:
//  - none: the owner is left without that role
//  - one: it takes over the role
//  - several: the user picks which one takes over the role
export type ContactRoles = { primary: boolean; accounting: boolean };
export type ContactReplacements = { primary: any | null; accounting: any | null };

type Props = {
  contact: any;
  roles: ContactRoles;
  candidates: any[];
  ownerName: string; // "customer" or "supplier"
  hasUnsavedChanges?: boolean;
  saving?: boolean;
  onConfirm: (replacements: ContactReplacements) => void;
  onCancel: () => void;
};

const fullName = (x: any) => x?.FullName || [x?.FirstName, x?.LastName].filter(Boolean).join(' ');
const describe = (x: any) => [fullName(x), x.EmailAddress || x.MobileNumber].filter(Boolean).join(' - ');

const ContactDeactivateModal = ({ contact, roles, candidates, ownerName, hasUnsavedChanges, saving, onConfirm, onCancel }: Props) => {
  const name = fullName(contact);
  const heldRoles = [roles.primary && 'primary', roles.accounting && 'accounting'].filter(Boolean) as string[];
  const rolesText = heldRoles.length ? `${heldRoles.join(' and ')} contact` : '';
  const mustChoose = heldRoles.length > 0 && candidates.length > 1;

  const [primaryId, setPrimaryId] = useState<string | null>(null);
  const [accountingId, setAccountingId] = useState<string | null>(null);

  const pick = (held: boolean, id: string | null) =>
    !held || candidates.length === 0 ? null : mustChoose ? candidates.find((x) => x.ID === id) ?? null : candidates[0];
  const replacements: ContactReplacements = {
    primary: pick(roles.primary, primaryId),
    accounting: pick(roles.accounting, accountingId),
  };
  const canConfirm = !saving && (!mustChoose
    || ((!roles.primary || !!replacements.primary) && (!roles.accounting || !!replacements.accounting)));

  const picker = (label: string, value: string | null, onChange: (v: string) => void) => (
    <Radio.Group label={label} value={value} onChange={onChange}>
      <ScrollArea.Autosize mah={'clamp(160px, 30vh, 300px)'} mt={'xs'}>
        <Stack gap={'xs'}>
          {candidates.map((x) => (
            <Radio key={x.ID} value={x.ID as string} label={describe(x)} color={'scBlue'} />
          ))}
        </Stack>
      </ScrollArea.Autosize>
    </Radio.Group>
  );

  return (
    <SCModal open={true} decor={'none'} onClose={onCancel} size={'md'}>
      <Stack gap={'md'} data-testid={'contact-active-status-modal'}>
        <Title size={'md'} fw={600}>
          {mustChoose ? `Select new ${rolesText}` : 'Deactivate contact'}
        </Title>

        {!heldRoles.length && <Text size={'sm'}>Are you sure you want to deactivate {name}?</Text>}

        {heldRoles.length > 0 && candidates.length === 0 && (
          <Text size={'sm'}>
            {name} is the only active contact. Once deactivated this {ownerName} will have no {rolesText}.
          </Text>
        )}

        {heldRoles.length > 0 && candidates.length === 1 && (
          <Text size={'sm'}>
            {name} is the {rolesText}. <b>{fullName(candidates[0])}</b> will become the {rolesText}.
          </Text>
        )}

        {mustChoose && (
          <>
            <Text size={'sm'}>
              {name} is the {rolesText}. Select which contact should take over.
            </Text>
            {roles.primary && picker('New primary contact', primaryId, setPrimaryId)}
            {roles.accounting && picker('New accounting contact', accountingId, setAccountingId)}
          </>
        )}

        {hasUnsavedChanges && (
          <Text size={'sm'} c={'yellow.8'}>
            You have unsaved changes to this contact. They will be discarded.
          </Text>
        )}

        <Group mt={'lg'} justify={'right'} gap={'xs'}>
          <Button type={'button'} variant={'subtle'} color={'gray.9'} onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
          <Button
            color={'red'}
            disabled={!canConfirm}
            rightSection={saving && <Loader variant={'oval'} size={18} color={'white'} />}
            onClick={() => onConfirm(replacements)}
          >
            Deactivate
          </Button>
        </Group>
      </Stack>
    </SCModal>
  );
};

export default ContactDeactivateModal;
